import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeaveApplications, useLeaveBalance } from '@saiens/shared/hooks';
import { getLeaveStateLabel, formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Plus, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';
import type { LeaveState } from '@saiens/shared/types';

const STATE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'f_approve', label: '待核准' },
  { value: 'agree', label: '已核准' },
  { value: 'refused', label: '已拒絕' },
];

function StateIcon({ state }: { state: LeaveState }) {
  switch (state) {
    case 'draft':
      return <FileText className="h-4 w-4 text-gray-400" />;
    case 'f_approve':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'agree':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'refused':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return null;
  }
}

export function LeaveListPage() {
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState('');

  const { data: balanceData } = useLeaveBalance();
  const { data: applicationsData, isLoading } = useLeaveApplications({
    state: stateFilter || undefined,
    limit: 50,
  });

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">請休假</h1>
        </div>
        <button
          onClick={() => navigate('/leave/apply')}
          className="flex items-center gap-1 rounded-lg bg-pink-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-pink-600"
        >
          <Plus className="h-4 w-4" />
          新增
        </button>
      </div>

      <div className="px-4 py-4">
        {/* Balance Cards */}
        {balanceData && balanceData.length > 0 && (
          <div className="mb-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-500">假別餘額</h2>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {balanceData.map((b) => (
                <div
                  key={`${b.type_id}-${b.validity_end}`}
                  className="flex-shrink-0 rounded-xl bg-white p-3 shadow-sm"
                  style={{ minWidth: '140px' }}
                >
                  <p className="text-xs font-medium text-gray-500">{b.type_name}</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {formatWorkHours(b.remaining_hours)}
                  </p>
                  <p className="text-xs text-gray-400">
                    共 {formatWorkHours(b.total_hours)}
                  </p>
                  {b.validity_end && (
                    <p className="mt-1 text-xs text-gray-400">
                      至 {b.validity_end.substring(5)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* State Filter */}
        <div className="mb-4 flex gap-2 overflow-x-auto">
          {STATE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStateFilter(f.value)}
              className={`flex-shrink-0 rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                stateFilter === f.value
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Applications List */}
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : applicationsData && applicationsData.items.length > 0 ? (
          <div className="space-y-2">
            {applicationsData.items.map((app) => (
              <button
                key={app.id}
                onClick={() => navigate(`/leave/${app.id}`)}
                className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <StateIcon state={app.state} />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {app.holiday_type_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {app.start_day}
                      {app.end_day && app.end_day !== app.start_day
                        ? ` ~ ${app.end_day}`
                        : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatWorkHours(app.holiday_time_total)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {getLeaveStateLabel(app.state)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">暫無請假記錄</p>
        )}
      </div>
    </div>
  );
}
